import { useMatches, useSearchParams, useSubmit } from '@remix-run/react'
import { useState, type Dispatch, type SetStateAction } from 'react'
import { cn } from '#app/utils/misc.js'


export type Tag = {
   id: string
   category?: 'pre-iconographic' | 'iconographic' | 'iconological'
   name: string
   safety?: string
   human?: number
   ai_gpt_4o?: number
   [x: string]: any
}


const Tabs = ['Human', 'AI'] as const
type Tab = (typeof Tabs)[number]


const contentClass = (category: Tag['category']) => {
   switch (category) {
       case 'pre-iconographic':
           return 'after:content-["pre-iconographic"]'
       case 'iconographic':
           return 'after:content-["iconographic"]'
       case 'iconological':
           return 'after:content-["iconological"]'
   }
}


const blockClass = (category: Tag['category'], isOnSearchPage: boolean) =>
   cn(
       'relative inline-block w-full rounded border border-gray-400 p-4 after:absolute after:-top-3 after:left-3 after:bg-gray-200 after:px-1 after:text-gray-800 dark:border-gray-500 after:dark:bg-gray-700 after:dark:text-gray-100',
       contentClass(category),
       isOnSearchPage
           ? 'after:bg-gray-200 after:dark:bg-gray-700'
           : 'after:bg-background after:dark:bg-background',
   )


   const TagsBlock = ({
	tags,
	setValue,
	action = '/search',
	onClick,
}: {
	tags: Tag[]
	setValue?: Dispatch<SetStateAction<string>>
	action?: string
	onClick?: (x: any) => void
}) => {
	const matches = useMatches()
	const isOnSearchPage = matches.some(
		m => m.pathname === '/search' || m.pathname.includes('/favorites'),
	)
	const [searchParams] = useSearchParams()
	const submit = useSubmit()
	const notSafe = searchParams.get('notSafe')

	const [activeTab, setActiveTab] = useState<Tab>('Human')
	const [language, setLanguage] = useState<string>('en')

	const activeTabTags =
		activeTab === 'Human'
			? tags.filter(tag => tag.human)
			: tags.filter(tag => tag.ai_gpt_4o)

	// Dynamically collect unique languages for the active tab
	const availableLanguages = Array.from(
		new Set(activeTabTags.map(tag => tag.language || 'en')),
	)

	// Ensure current language is valid; fallback to first available
	const currentLanguage = availableLanguages.includes(language)
		? language
		: availableLanguages[0]

	const filteredTags = activeTabTags.filter(tag => tag.language === currentLanguage)

	const groupedByCategory = {
		'pre-iconographic': filteredTags.filter(t => t.category === 'pre-iconographic'),
		iconographic: filteredTags.filter(t => t.category === 'iconographic'),
		iconological: filteredTags.filter(t => t.category === 'iconological'),
	}

	return (
		<div className="relative w-full flex flex-col gap-4">
			<div className="flex gap-2">
				{Tabs.map(tab => (
					<button
						key={tab}
						onClick={() => setActiveTab(tab)}
						className={cn(
							'px-4 py-1 rounded border text-sm',
							activeTab === tab
								? 'bg-primary text-primary-foreground'
								: 'bg-muted text-muted-foreground',
						)}
					>
						{tab}
					</button>
				))}
			</div>

			{availableLanguages.length > 0 && (
				<div className="flex gap-2">
					{availableLanguages.map(lang => (
						<button
							key={lang}
							onClick={() => setLanguage(lang)}
							className={cn(
								'px-2 py-1 rounded border text-xs',
								currentLanguage === lang
									? 'bg-primary text-primary-foreground'
									: 'bg-muted text-muted-foreground',
							)}
						>
							{lang.toUpperCase()}
						</button>
					))}
				</div>
			)}

			{(Object.entries(groupedByCategory) as [Tag['category'], Tag[]][]).map(
				([category, catTags]) =>
					catTags.length > 0 && (
						<div key={category} className={blockClass(category, isOnSearchPage)}>
							<div className="flex flex-wrap gap-1">
								{catTags.map(tag => (
									<span
										key={tag.id}
										onClick={
											onClick
												? () => onClick(tag.name)
												: () => {
														submit(
															{
																search: tag.name,
																...(notSafe ? { notSafe } : {}),
															},
															{ method: 'GET', action },
														)
														setValue && setValue(tag.name)
													}
										}
										className={cn(
											'transform cursor-pointer rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset transition-transform duration-200 hover:-translate-y-0.5',
											tag.safety === 'not safe'
												? 'bg-red-200 text-red-700 ring-red-600/20'
												: 'bg-green-50 text-green-700 ring-green-600/20',
										)}
									>
										{tag.name}
									</span>
								))}
							</div>
						</div>
					),
			)}
		</div>
	)
}


export default TagsBlock


